import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { Input } from '@ordo/ui/input'
import { Separator } from '@ordo/ui/separator'
import { Plus, Trash2, X } from 'lucide-react'
import { useCallback, useState } from 'react'
import type { AssociationEntry } from 'types/contacts'

export interface PendingAssociations {
  schoolLocations: AssociationEntry[]
  vendorLocations: AssociationEntry[]
  organizations: AssociationEntry[]
}

type AssociationKey = keyof PendingAssociations

interface PendingSectionProps {
  label: string
  kind: AssociationKey
  items: AssociationEntry[]
  onAdd: (kind: AssociationKey, entry: AssociationEntry) => void
  onRemove: (kind: AssociationKey, index: number) => void
}

function PendingSection({ label, kind, items, onAdd, onRemove }: PendingSectionProps) {
  const [adding, setAdding] = useState(false)
  const [newExternalId, setNewExternalId] = useState('')
  const [newRole, setNewRole] = useState('')

  const handleAdd = useCallback(() => {
    if (!newExternalId.trim() || !newRole.trim()) {
      return
    }
    onAdd(kind, { externalId: newExternalId.trim(), role: newRole.trim() })
    setNewExternalId('')
    setNewRole('')
    setAdding(false)
  }, [kind, newExternalId, newRole, onAdd])

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-muted-foreground">
          {label} ({items.length})
        </label>
        {!adding && (
          <Button type="button" variant="outline" size="sm" onClick={() => setAdding(true)}>
            <Plus className="mr-1 h-3 w-3" />
            Add
          </Button>
        )}
      </div>

      {items.length === 0 && !adding && <p className="text-sm text-muted-foreground">None</p>}

      {items.length > 0 && (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="px-3 py-2 text-left font-medium text-muted-foreground">ID</th>
                <th className="px-3 py-2 text-left font-medium text-muted-foreground">Role</th>
                <th className="w-12 px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {items.map((entry, idx) => (
                <tr key={`${entry.externalId}-${entry.role}`} className="border-b border-border last:border-0">
                  <td className="px-3 py-2">
                    <Badge variant="secondary" className="font-mono text-xs">
                      {entry.externalId}
                    </Badge>
                  </td>
                  <td className="px-3 py-2">{entry.role}</td>
                  <td className="px-3 py-2">
                    <Button type="button" variant="ghost" size="sm" onClick={() => onRemove(kind, idx)}>
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adding && (
        <div className="flex items-end gap-2">
          <div className="flex-1 space-y-1">
            <label className="text-xs text-muted-foreground">External ID</label>
            <Input value={newExternalId} onChange={(e) => setNewExternalId(e.target.value)} className="h-8 text-sm" />
          </div>
          <div className="flex-1 space-y-1">
            <label className="text-xs text-muted-foreground">Role</label>
            <Input value={newRole} onChange={(e) => setNewRole(e.target.value)} className="h-8 text-sm" />
          </div>
          <Button type="button" size="sm" onClick={handleAdd}>
            Add
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setAdding(false)
              setNewExternalId('')
              setNewRole('')
            }}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  )
}

export function CreateAssociations({
  associations,
  onChange,
}: {
  associations: PendingAssociations
  onChange: (associations: PendingAssociations) => void
}) {
  const handleAdd = useCallback(
    (kind: AssociationKey, entry: AssociationEntry) => {
      onChange({ ...associations, [kind]: [...associations[kind], entry] })
    },
    [associations, onChange],
  )

  const handleRemove = useCallback(
    (kind: AssociationKey, index: number) => {
      onChange({
        ...associations,
        [kind]: associations[kind].filter((_, i) => i !== index),
      })
    },
    [associations, onChange],
  )

  return (
    <div className="space-y-4">
      <Separator />
      <PendingSection
        label="School Locations"
        kind="schoolLocations"
        items={associations.schoolLocations}
        onAdd={handleAdd}
        onRemove={handleRemove}
      />
      <PendingSection
        label="Vendor Locations"
        kind="vendorLocations"
        items={associations.vendorLocations}
        onAdd={handleAdd}
        onRemove={handleRemove}
      />
      <PendingSection
        label="Organizations"
        kind="organizations"
        items={associations.organizations}
        onAdd={handleAdd}
        onRemove={handleRemove}
      />
    </div>
  )
}
